/**
 * Device + initials identity persisted in localStorage.
 *
 * Anonymous players are identified by `deviceId` — a UUID generated on
 * first run and kept under `arcadia84:identity:device-id-v1`. The server
 * (sc-3014) provisions a `players` row per device on the first score
 * submission. When the player later signs in (sc-3015), the claim flow
 * upgrades that same row by attaching the Zyphr user ID.
 *
 * `lastInitials` defaults the initials-entry UI — most players reuse
 * the same 3 letters across runs.
 */

const DEVICE_ID_KEY = 'arcadia84:identity:device-id-v1';
const INITIALS_KEY = 'arcadia84:identity:initials-v1';

function uuidv4(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for older browsers — same v4 format.
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Returns this device's persistent UUID, creating + storing one on first
 * call. Subsequent calls return the same value across reloads.
 */
export function getOrCreateDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(existing)) {
      return existing;
    }
    const fresh = uuidv4();
    localStorage.setItem(DEVICE_ID_KEY, fresh);
    return fresh;
  } catch {
    // localStorage unavailable (private mode, etc.). Return a per-tab
    // UUID — scores submitted in this session land under a "fresh"
    // anonymous player every page load, which is degraded but functional.
    return uuidv4();
  }
}

export function getLastInitials(): string | null {
  try {
    const raw = localStorage.getItem(INITIALS_KEY);
    if (raw && /^[A-Z]{1,3}$/.test(raw)) return raw;
    return null;
  } catch {
    return null;
  }
}

export function setLastInitials(initials: string): void {
  if (!/^[A-Z]{1,3}$/.test(initials)) return;
  try {
    localStorage.setItem(INITIALS_KEY, initials);
  } catch {
    /* ignore */
  }
}
